import React from 'react';
import PropTypes from 'prop-types';
import Rule from '../Rule';
import Group from '../Group';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

const MAX_DEPTH = 4;
const MAX_RULES = 50;

function withSearchTree (WrappedComponent) {
    class WithSearchTree extends React.Component {
        static defaultProps = {
            depth: 0
        };

        static propTypes = {
            tree: PropTypes.object.isRequired,
            depth: PropTypes.number.isRequired,
            fields: PropTypes.object.isRequired,
            onChange: PropTypes.func.isRequired,
            onDelete: PropTypes.func.isRequired
        };

        constructor (props) {
            super(props);

            this.handleConjunctionChange = this.handleConjunctionChange.bind(this);

            this.handleAddRuleClick = this.handleAddRuleClick.bind(this);
            this.handleAddGroupClick = this.handleAddGroupClick.bind(this);
            this.handleDeleteClick = this.handleDeleteClick.bind(this);

            this.branch = this.branch.bind(this);
            this.handleChangeBranch = this.handleChangeBranch.bind(this);
            this.handleDeleteBranch = this.handleDeleteBranch.bind(this);
        }

        handleConjunctionChange (event, conjunction) {
            const { tree, onChange } = this.props;
            if (conjunction !== null) {
                const newTree = { ...tree };
                newTree.conjunction = conjunction;
                onChange(newTree);
            }
        }

        handleAddRuleClick () {
            const { tree, onChange } = this.props;
            if (tree.children.length > MAX_RULES) { return; }        
            const newTree = { ...tree };
            newTree.children = [
                ...(newTree.children || []),
                {
                    id: uuidv4(),
                    type: 'rule',
                    field: null,
                    isValid: false
                }
            ];
            onChange(newTree);
        }

        handleAddGroupClick () {
            const { tree, depth, onChange } = this.props;
            if (depth > MAX_DEPTH && tree.children.length < MAX_RULES) { return; }
            const newTree = { ...tree };
            newTree.children = [
                ...(newTree.children || []),
                {
                    id: uuidv4(),
                    type: 'group',
                    conjunction: 'and',
                    children: []
                }
            ];
            onChange(newTree);
        }


        handleChangeBranch (branch) {
            const { tree, onChange } = this.props;

            // ToDo Perhaps check children recursively for a max total of rules, send error up chain if too many
            const idx = tree.children.findIndex(el => el.id === branch.id);
            if (idx === -1) {
                console.error(`Failed to find id ${branch.id} for tree with id ${tree.id}`);
                return;
            }
            
            const newTree = { ...tree };
            newTree.children[idx] = branch;
        
            onChange(newTree);
        }

        branch (child) {
            const { tree, depth } = this.props;

            // Fields is the list of options to select between for a rule
            let fields;
            if (tree.type === 'group-rule') {
                switch (tree.field) {
                    case 'intersects':
                    case 'notintersects':
                    case 'within':
                    case 'notwithin':
                        // Always have active to layer root?
                        fields = this.props.fields[tree.properties.layer].subfields;
                        break;
                    default:
                        fields = this.props.fields[tree.field].subfields;
                }
            } else {
                fields = this.props.fields;
            }

            switch (child.type) {
                case 'group':
                    return <Group
                        key={child.id}
                        tree={child}
                        depth={depth + 1}
                        fields={fields}
                        onChange={this.handleChangeBranch}
                        onDelete={this.handleDeleteBranch}
                    />;
                case 'group-rule':
                case 'rule':
                    return <Rule
                        key={child.id}
                        tree={child}
                        depth={depth + 1}
                        fields={fields}
                        onChange={this.handleChangeBranch}
                        onDelete={this.handleDeleteBranch}
                    />;
                default:
                    console.error('Unsupported type');
                    return <></>;
            }
        }

        // Given id of branch to delete, removes that child
        handleDeleteBranch (id) {
            const { tree, onChange } = this.props;

            const idx = tree.children.findIndex(el => el.id === id);
            if (idx === -1) {
                console.error(`Failed to find id ${id} for tree with id ${tree.id}`);
                return;
            }
            const newTree = { ...tree };
            const newChildren = [...newTree.children];
            newChildren.splice(idx, 1);
            newTree.children = newChildren;
            onChange(newTree);
        }

        // To delete self, parent must remove from children
        handleDeleteClick () {
            const { tree, onDelete } = this.props;
            onDelete(tree.id);
        }

        render () {
            return <WrappedComponent
                {...this.props}
                onBranch={this.branch}
                onAddRule={this.handleAddRuleClick}
                onAddGroup={this.handleAddGroupClick}
                onConjunctionChange={this.handleConjunctionChange}
                onDelete={this.handleDeleteClick}
            />;
        }
    }

    const wrappedComponentName = WrappedComponent.displayName
        || WrappedComponent.name
        || 'Component';

    WithSearchTree.displayName = `withSearchTree(${wrappedComponentName})`;
    return WithSearchTree;
}

export default withSearchTree;